import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AuthContext from "../../context/auth/AuthContext";
import { getUserDisplayName } from "../../helper/common";
import useTranslator from "../../hooks/useTranslator";
import LandLordContext from "../../context/landlord/LandLordContext";
import { calculateParticipantRentAmount } from "../../helper/profileCalculations";
import { convertCurrencyFormat } from "../../helper/common";
import {
  calculateNowTime,
  getProfileReminders,
} from "../../services/sharedService";
import moment from "moment";
import DocsStatus from "./DocsStatus";
import { Avatar, Badge, Icon } from "../design";
import LoadingSpinner from "../LoadingSpinner";
import ClientIdentificationStatus, {
  isClientIdentificationPending,
} from "./ClientIdentificationStatus";

const AccordionParticipantRow = ({
  profile,
  participant,
  isPending,
  isOwner,
  tenantChecks = false,
  clientIdentificationRule = false,
}) => {
  const { _t } = useTranslator();
  const authContext = useContext(AuthContext);
  const { user, checkIeDetails } = authContext;
  const landLordContext = useContext(LandLordContext);
  const { deaWorkspace } = landLordContext;
  const navigate = useNavigate();
  const { workspaceId } = useParams();

  const tenantInfo = participant.tenant_info;
  const isDeleted = participant.is_deleted === true;
  const clientIdentificationPending = isClientIdentificationPending(
    tenantInfo,
    clientIdentificationRule
  );

  const rentAmount = calculateParticipantRentAmount(
    tenantInfo,
    { ...participant, ...profile },
    user,
    _t,
    deaWorkspace
  );

  const calculateScore = (userDetails) => {
    if (checkIeDetails(userDetails)) {
      const totalScore =
        (userDetails?.paymentScores?.creditScore?.value || 0) / 2;
      return totalScore > 850 ? 850 : totalScore;
    }
    return userDetails?.paymentScores?.creditScore?.value || 0;
  };

  const getConsentStatusDisplay = () => {
    if (isPending) {
      return <Badge tone="warning">{_t("pendingSharingProfile")}</Badge>;
    }
    return <Badge>{_t("active", "Active")}</Badge>;
  };

  const missingCount = participant?.tenant_info?.missingCount || 0;
  const [reminderSentAt, setReminderSentAt] = useState(null);

  useEffect(() => {
    const tenantId = tenantInfo?._id;
    if (!tenantInfo?.is_outdated || !tenantId) {
      setReminderSentAt(null);
      return;
    }
    let cancelled = false;
    getProfileReminders(tenantId)
      .then((response) => {
        if (!cancelled) {
          setReminderSentAt(response?.data?.[0]?.sent_at || null);
        }
      })
      .catch(() => {
        if (!cancelled) setReminderSentAt(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantInfo?.is_outdated, tenantInfo?._id]);

  const participantName = isPending
    ? participant.email
    : isDeleted
      ? _t("deletedUser", "Deleted user")
      : getUserDisplayName(tenantInfo);

  const isGuarantor = participant.is_guarantor === true && !isDeleted;

  const participantRole = isDeleted
    ? ""
    : isPending
      ? _t("pending")
      : isOwner
        ? _t("owner")
        : _t("flatmate", "Compañero/a");

  const initials = (participantName || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0]?.toUpperCase())
    .join("");

  const handleOpenProfile = () => {
    if (
      isDeleted ||
      isPending ||
      !tenantInfo?._id ||
      participant?.tenant_info?.forensic_status !== "COMPLETED"
    )
      return;

    navigate(
      `/${workspaceId}/profile-property?sharedprofileid=${profile?._id}&tenant_id=${tenantInfo?._id}`
    );
  };

  return (
    <>
      <tr
        className={`hideOnMobile second-row relative participant-row participant-row-clean ${
          !isDeleted &&
          !isPending &&
          tenantInfo?._id &&
          participant?.tenant_info?.forensic_status === "COMPLETED"
            ? "cursor-pointer"
            : ""
        } ${tenantInfo?.is_outdated ? "dimmedRow" : ""}`}
        style={isDeleted ? { opacity: 0.6 } : undefined}
        onClick={handleOpenProfile}
      >
        <td>
          <div className="profile-cell-tenant profile-cell-tenant--nested">
            {isDeleted ? (
              <Avatar src="/images/deleted-user-icon.svg" size="sm" />
            ) : (
              <Avatar name={participantName || "U"} size="sm" />
            )}
            <div className="d-flex flex-column">
              <span className="participant-nameRow">
                <span className="participant-name" title={participantName}>
                  {participantName}
                </span>
                {isGuarantor && (
                  <Badge tone="neutral" size="xs">
                    {_t("guarantor", "Guarantor")}
                  </Badge>
                )}
              </span>
              <span className="participant-role">{participantRole}</span>
            </div>
          </div>
        </td>

        <td className="skor-details-column">
          {isPending || isDeleted ? "-" : calculateScore(tenantInfo)}
        </td>

        <td>
          {isPending || isDeleted
            ? "-"
            : `€${convertCurrencyFormat(rentAmount.combinedIncome)}`}
        </td>

        <td>
          {isPending || isDeleted
            ? "-"
            : `€${convertCurrencyFormat(rentAmount.effort_rate)}`}
        </td>

        {/* <td>
          <div style={{ marginInline: "0.375rem" }}>
            {isPending ? "-" : tenantInfo?.sharers?.length || 0}
          </div>
        </td> */}

        <td>
          <div style={{ marginInline: "0.375rem" }}>
            {isPending || isDeleted
              ? "-"
              : Array.isArray(tenantInfo?.gurantor_id)
                ? tenantInfo.gurantor_id.length
                : 0}
          </div>
        </td>
        <td></td>

        {!tenantChecks && (
          <td>
            <DocsStatus
              missingCount={missingCount}
              isPending={isPending || isDeleted}
            />
          </td>
        )}

        <td>
          {isDeleted ? (
            "-"
          ) : isPending ? (
            getConsentStatusDisplay()
          ) : participant?.tenant_info?.forensic_status !== "COMPLETED" ? (
            <Badge tone="neutral" className="profile-badge-processing">
              <span className="ds-spinner" aria-hidden="true" />
              {_t("processingProfile")}
            </Badge>
          ) : tenantInfo?.is_outdated ? (
            <div
              className="d-flex flex-column"
              style={{ alignItems: "flex-start", gap: "2px" }}
            >
              <Badge tone="warning">{_t("outdated")}</Badge>
              {reminderSentAt && (
                <span
                  className="profileReminderDate"
                  style={{ fontSize: "var(--text-11)" }}
                >
                  {_t("reminderSent", "Reminder sent")}{" "}
                  {moment(reminderSentAt)
                    .locale(
                      (localStorage.getItem("i18nextLng") || "").startsWith(
                        "es"
                      )
                        ? "es"
                        : "en-gb"
                    )
                    .fromNow()}
                </span>
              )}
            </div>
          ) : clientIdentificationPending ? (
            <ClientIdentificationStatus />
          ) : (
            <div
              className="d-flex align-items-center custom-gap-2"
              style={{ fontSize: "var(--text-13)" }}
            >
              {calculateNowTime(
                profile?.sortDate,
                localStorage.getItem("i18nextLng")
              )}
            </div>
          )}
          {/* <div className="participant-status-cell">
            {getConsentStatusDisplay()}
          </div> */}
        </td>

        <td>
          <span className="profile-row-open">
            <Icon name="chevronRight" size={18} />
          </span>
        </td>
      </tr>

      <div
        className={`hideOnDesktop accordion-tile ${
          !isDeleted &&
          !isPending &&
          tenantInfo?._id &&
          participant?.tenant_info?.forensic_status !== "COMPLETED"
            ? "cursor-pointer"
            : ""
        } ${tenantInfo?.is_outdated ? "dimmedMobileCard" : ""}`}
        style={isDeleted ? { opacity: 0.6 } : undefined}
        onClick={handleOpenProfile}
      >
        <div
          className="mobile-profile-info-box d-flex align-items-center mobile-profile-gap"
          style={{ paddingLeft: "2vw" }}
        >
          <div className="participant-initials-avatar d-flex align-items-center justify-content-center">
            {isDeleted ? (
              <img
                src="/images/deleted-user-icon.svg"
                alt={_t("deletedUser", "Deleted user")}
                width={16}
                height={16}
              />
            ) : (
              initials || "U"
            )}
          </div>
          <div className="d-flex flex-column">
            <span className="participant-nameRow">
              <span className="participant-name" title={participantName}>
                {participantName}
              </span>
              {isGuarantor && (
                <Badge tone="neutral" size="xs">
                  {_t("guarantor", "Guarantor")}
                </Badge>
              )}
            </span>
            <span className="participant-role">{participantRole}</span>
          </div>
        </div>

        <div className="mobile-profile-details">
          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("skor")}</span>
            <span className="detail-value">
              {isPending || isDeleted ? "-" : calculateScore(tenantInfo)}
            </span>
          </div>

          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("income")}</span>
            <span className="detail-value">
              {isPending || isDeleted
                ? "-"
                : `€${convertCurrencyFormat(rentAmount.combinedIncome)}`}
            </span>
          </div>

          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("maxRent")}</span>
            <span className="detail-value">
              {isPending || isDeleted
                ? "-"
                : `€${convertCurrencyFormat(rentAmount.effort_rate)}`}
            </span>
          </div>

          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("flatmates")}</span>
            <span className="detail-value">
              {isPending || isDeleted ? "-" : tenantInfo?.sharers?.length || 0}
            </span>
          </div>

          <div className="mobile-profile-detail-item">
            <span className="detail-label">{_t("guarantors")}</span>
            <span className="detail-value">
              {isPending || isDeleted
                ? "-"
                : Array.isArray(tenantInfo?.gurantor_id)
                  ? tenantInfo.gurantor_id.length
                  : 0}
            </span>
          </div>

          {!tenantChecks && (
            <div className="mobile-profile-detail-item">
              <span className="detail-label">{_t("docs")}</span>
              <span className="detail-value">
                <DocsStatus
                  missingCount={missingCount}
                  isPending={isPending || isDeleted}
                />
              </span>
            </div>
          )}
        </div>

        <div className="mobile-profile-footer">
          <div className="d-flex custom-gap-2 align-items-center">
            {/* <div className="participant-status-cell">
              {getConsentStatusDisplay()}
            </div> */}
            {!isDeleted &&
              !isPending &&
              (participant?.tenant_info?.forensic_status !== "COMPLETED" ? (
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "0.375rem",
                    borderRadius: "var(--radius-pill)",
                    padding: "0.1875rem 0.5rem",
                    fontSize: "var(--text-12)",
                    fontWeight: 600,
                    lineHeight: 1,
                    background: "var(--surface-muted)",
                    color: "var(--text-muted)",
                    border: "1px solid var(--text-muted)",
                  }}
                >
                  <LoadingSpinner
                    size={"11px"}
                    color="rgb(127 127 127)"
                    borderWidth={2}
                    className={"docs-loading-spinner"}
                  />
                  <span style={{ color: "var(--text-muted)" }}>
                    {_t("processingProfile")}
                  </span>
                </div>
              ) : tenantInfo?.is_outdated ? (
                <div
                  className="d-flex flex-column"
                  style={{ alignItems: "flex-start", gap: "2px" }}
                >
                  <Badge tone="warning">{_t("outdated")}</Badge>
                  {reminderSentAt && (
                    <span
                      className="profileReminderDate"
                      style={{ fontSize: "var(--text-11)" }}
                    >
                      {_t("reminderSent", "Reminder sent")}{" "}
                      {moment(reminderSentAt)
                        .locale(
                          (localStorage.getItem("i18nextLng") || "").startsWith(
                            "es"
                          )
                            ? "es"
                            : "en-gb"
                        )
                        .fromNow()}
                    </span>
                  )}
                </div>
              ) : clientIdentificationPending ? (
                <ClientIdentificationStatus />
              ) : (
                <div
                  className="d-flex align-items-center custom-gap-2"
                  style={{ fontSize: "var(--text-11)" }}
                >
                  {calculateNowTime(
                    profile?.sortDate,
                    localStorage.getItem("i18nextLng")
                  )}
                </div>
              ))}
          </div>
          {!isDeleted && !isPending && tenantInfo?._id && (
            <div className="cursor-pointer">
              <img
                className="arrowRightTable"
                src="/images/arrow-right-green.svg"
                alt="Open participant"
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AccordionParticipantRow;
